/**
 * The running order, the room, and the wording — everything about a docket that is not a vote.
 *
 * This replaces `DocketVote`, which did all of the above AND rendered three vote buttons per GM
 * the moment a commissioner opened the pane: before the docket was published, let alone before the
 * meeting reached the item. Voting moved to `Meeting`, behind `canVote`. What was left was this,
 * and it turned out to be most of the value — a commissioner spends far longer arranging a docket
 * than recording votes on it.
 *
 * Grouping by topic is the one editorial decision here: related rules get argued together, and a
 * docket that alternates between keepers and playoffs makes the room re-orient six times.
 */

import { useState } from 'react';
import { managerShort } from '../domain/identity.js';
import { docketNumbers, proposersOf } from '../domain/proposals.js';
import { GmBadge } from './GmBadge.js';
import { GMS } from '../store/session.js';
import type { Docket, RuleProposal } from '../store/types.js';

export interface DocketOrderProps {
  agenda: RuleProposal[];
  docket: Docket | null;
  onMove: (p: RuleProposal, by: number) => void;
  onRemove: (p: RuleProposal) => void;
  onSetAbsent: (absent: string[]) => void;
  onBranch: (p: RuleProposal) => void;
  onEditWording: (p: RuleProposal) => void;
  onToggleObjection: (p: RuleProposal) => void;
}

export function DocketOrder(props: DocketOrderProps) {
  const { agenda, docket } = props;
  const [marking, setMarking] = useState(false);
  const absent = new Set((docket?.absent ?? []).map((a) => a.toUpperCase()));
  const numbers = docketNumbers(
    agenda.map((p) => p.id),
    new Map(agenda.map((p) => [p.id, { branchedFrom: p.branchedFrom }])),
  );
  /* Reordering is a commissioner's job until the meeting opens; after that the order is the
     record of what was discussed and moving an item would rewrite it. */
  const locked = Boolean(docket?.meetingOpenedAt);

  return (
    <section className="dkorder">
      <div className="striptop">
        <h3 className="striphead">Running order</h3>
        {locked && <span className="mut small">Locked — the meeting has opened</span>}
      </div>

      <ol className="dklist">
        {agenda.map((p, i) => (
          <li key={p.id} className="dkrow">
            <span className="dkno">{numbers.get(p.id) ?? String(i + 1)}</span>
            <span className="dkwho">
              {proposersOf(p).map((g) => <GmBadge key={g} gm={g} size="sm" />)}
            </span>
            <span className="dktitle">
              <b>{p.title}</b>
              {p.isRevote && <span className="chip warning">re-vote</span>}
              {p.branchedFrom && <span className="chip">amendment</span>}
              {p.proposerObjects && <span className="chip critical">proposer objects</span>}
            </span>
            <span className="dkacts">
              {!locked && (
                <>
                  <button type="button" className="linkbtn tiny" disabled={i === 0}
                    onClick={() => props.onMove(p, -1)} aria-label="Move up">↑</button>
                  <button type="button" className="linkbtn tiny" disabled={i === agenda.length - 1}
                    onClick={() => props.onMove(p, 1)} aria-label="Move down">↓</button>
                  <button type="button" className="linkbtn tiny"
                    onClick={() => props.onRemove(p)}>remove</button>
                </>
              )}
              <button type="button" className="linkbtn tiny"
                onClick={() => props.onEditWording(p)}>wording</button>
              <button type="button" className="linkbtn tiny"
                onClick={() => props.onBranch(p)}>amend</button>
              {/*
                2020-I-1 §2: an original proposer being against a modification made in the room
                vetoes it regardless of the count. It is a fact about the proposal, not a vote,
                which is why it stays here.
              */}
              <button type="button" className="linkbtn tiny"
                onClick={() => props.onToggleObjection(p)}>
                {p.proposerObjects ? 'clear objection' : 'proposer objects'}
              </button>
            </span>
          </li>
        ))}
        {agenda.length === 0 && <li className="note">Nothing on the docket yet.</li>}
      </ol>

      {/*
        WHO IS IN THE ROOM, in people rather than in a number. `represented` is derived from this
        and drives the relaxed six-vote threshold (2015-I-1), so getting it wrong changes outcomes.
      */}
      <div className="dkroom">
        <div className="striptop">
          <h4 className="striphead small">In the room</h4>
          <button type="button" className="linkbtn tiny" onClick={() => setMarking((v) => !v)}>
            {marking ? 'done' : 'mark attendance'}
          </button>
        </div>
        <ul className="dkgms">
          {GMS.map((g) => {
            const away = absent.has(g.toUpperCase());
            return (
              <li key={g} className={`dkgm ${away ? 'away' : ''}`}>
                {marking ? (
                  <button type="button" className={`dkmark ${away ? 'away' : ''}`}
                    onClick={() => props.onSetAbsent(
                      away
                        ? [...absent].filter((a) => a !== g.toUpperCase())
                        : [...absent, g.toUpperCase()],
                    )}>
                    <GmBadge gm={g} size="sm" />
                    <span>{managerShort(g)}</span>
                  </button>
                ) : (
                  <>
                    <GmBadge gm={g} size="sm" />
                    <span>{managerShort(g)}</span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
        <p className="mut small">
          {GMS.length - absent.size} of {GMS.length} represented.
          {absent.size > 0 && ' A relaxed six-vote pass carries one season only.'}
        </p>
      </div>
    </section>
  );
}
